// client/src/hooks/useProfissionais.js - VERSÃO 100% OTIMIZADA PARA MULTITENANT
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import useAlert from './useAlert';

const PROFISSIONAIS_QUERY_KEY = 'profissionais';

// Contrato canônico do payload — ver bloco de documentação em
// server/controllers/profissionaisController.js (CONTRATO CANÔNICO —
// ProfissionalPayload).

// ==============================================================================
// ✅ UTILITÁRIOS
// ==============================================================================

const validateId = (id, operation = 'operação') => {
    if (!id) return null;
    
    const idToValidate = id?._id || id?.id || id;
    
    if (!idToValidate) return null;
    
    const idStr = String(idToValidate).trim();
    
    if (!/^[0-9a-fA-F]{24}$/.test(idStr)) {
        console.warn(`[validateId] ID com formato inválido para ${operation}:`, idStr);
        return null;
    }
    
    return idStr;
};

const buildQueryParams = (params = {}) => {
    const queryParams = { ...params };
    
    const booleanFields = ['disponivel', 'ativos', 'isActive'];
    booleanFields.forEach(field => {
        if (queryParams[field] !== undefined) {
            queryParams[field] = queryParams[field] === true || queryParams[field] === 'true';
        }
    });
    
    Object.keys(queryParams).forEach(key => {
        const value = queryParams[key];
        
        // Mantém para avisar o backend caso queira ver inativos
        if (key === 'status' && value === 'todos') {
            return;
        }

        if (value === undefined || value === null || value === '' || 
            (Array.isArray(value) && value.length === 0)) {
            delete queryParams[key];
        }
    });
    
    return queryParams;
};

const extractData = (response) => {
    if (!response || !response.data) {
        console.warn('[extractData] Resposta vazia ou sem data:', response);
        return null;
    }
    
    if (response.data.success !== undefined) {
        return response.data.data || response.data;
    }
    
    return response.data;
};

const formatProfissionalData = (profissional) => {
    if (!profissional) return null;

    const isProfissionalInativo = profissional.status === 'inativo' || profissional.status === 'desligado';
    
    return {
        id: profissional.id || profissional._id,
        userId: profissional.user?.id || profissional.user?._id || profissional.user,
        accountId: profissional.account?.id || profissional.account?._id || profissional.account,
        user: profissional.user ? {
            id: profissional.user.id || profissional.user._id,
            name: profissional.user.name || '',
            email: profissional.user.email || null,
            telefone: profissional.user.telefone || '',
            photo: profissional.user.photo || null,
            photoUrl: profissional.user.photoUrl || '',
            avatarUrl: profissional.user.avatarUrl || '',
            isActive: !isProfissionalInativo && profissional.user.isActive !== false
        } : null,

        especialidades: profissional.especialidades || [],
        registroProfissional: profissional.registroProfissional || '',

        unidadesCount: profissional.unidadesCount || profissional.estatisticas?.totalUnidades || 0,
        servicosCount: profissional.servicosCount || profissional.estatisticas?.totalServicos || 0,

        status: profissional.status || 'ativo',
        disponivelParaAgendamento: profissional.disponivelParaAgendamento !== false,
        isDisponivel: !isProfissionalInativo && profissional.disponivelParaAgendamento !== false,

        configuracoesAgenda: profissional.agendaDefinicoes?.configuracoes || {
            permiteAgendamentoOnline: true,
            intervaloGradeHorarios: 60,
            horarioInicio: 7,
            horarioFim: 22
        },

        metadata: {
            corAgenda: profissional.metadata?.corAgenda || '#3788d8',
            exibirNaAgenda: profissional.metadata?.exibirNaAgenda !== false,
            descricaoCurta: profissional.metadata?.descricaoCurta || '',
            biografia: profissional.metadata?.biografia || ''
        },

        totalEspecialidades: profissional.especialidades?.length || 0,
        primeiraEspecialidade: profissional.especialidades?.[0] || null,

        criadoPor: profissional.criadoPor,
        atualizadoPor: profissional.atualizadoPor,
        createdAt: profissional.createdAt,
        updatedAt: profissional.updatedAt,

        relacionamentos: profissional.relacionamentos || null,
        estatisticas: profissional.estatisticas || null,

        statusDisplay: profissional.status === 'ativo' ? 'Ativo' :
                       profissional.status === 'inativo' ? 'Inativo' :
                       profissional.status === 'ferias' ? 'Em Férias' :
                       profissional.status === 'licenca' ? 'Em Licença' :
                       profissional.status === 'desligado' ? 'Desligado' : profissional.status
    };
};

// ==============================================================================
// ✅ FUNÇÕES DE FETCH
// ==============================================================================

const fetchProfissionais = async (params = {}) => {
    const queryParams = buildQueryParams(params);
    const response = await api.get('/profissionais', { params: queryParams });
    const resData = response.data;
    
    if (resData && resData.success && Array.isArray(resData.data)) {
        return {
            ...resData,
            data: resData.data.map(prof => formatProfissionalData(prof))
        };
    }
    
    return resData;
};

const fetchProfissionalById = async (id) => {
    const validId = validateId(id, 'busca de profissional');
    if (!validId) return null;
    
    try {
        const response = await api.get(`/profissionais/${validId}`);
        const data = extractData(response);
        return formatProfissionalData(data);
    } catch (error) {
        if (error.response?.status === 404) {
            console.warn(`[fetchProfissionalById] Profissional ${validId} não encontrado.`);
            return null;
        }
        throw error;
    }
};

const fetchProfissionaisDisponiveis = async (params = {}) => {
    const queryParams = buildQueryParams(params);
    const response = await api.get('/profissionais/disponiveis', { params: queryParams });
    const data = extractData(response);
    
    if (Array.isArray(data)) {
        return data.map(prof => formatProfissionalData(prof));
    }
    
    return data?.data?.map?.(prof => formatProfissionalData(prof)) || [];
};

const fetchProfissionaisPorEspecialidade = async (especialidade, params = {}) => {
    if (!especialidade) return [];
    
    const queryParams = buildQueryParams(params);
    const response = await api.get(`/profissionais/busca/especialidade/${encodeURIComponent(especialidade)}`, { params: queryParams });
    const data = extractData(response);
    
    if (Array.isArray(data)) {
        return data.map(prof => formatProfissionalData(prof));
    }
    
    return data?.data?.map?.(prof => formatProfissionalData(prof)) || [];
};

const fetchProfissionaisPorServico = async (servicoId, params = {}) => {
    const validId = validateId(servicoId, 'busca por serviço');
    if (!validId) return [];
    
    const queryParams = buildQueryParams(params);
    const response = await api.get(`/profissionais/servico/${validId}`, { params: queryParams });
    const data = extractData(response);
    
    if (Array.isArray(data)) {
        return data.map(prof => formatProfissionalData(prof));
    }
    
    return data?.data?.map?.(prof => formatProfissionalData(prof)) || [];
};

const fetchProfissionalPorUsuario = async (userId) => {
    const validId = validateId(userId, 'busca por usuário');
    if (!validId) return null;
    
    try {
        const response = await api.get(`/profissionais/usuario/${validId}`);
        const data = extractData(response);
        return formatProfissionalData(data);
    } catch (error) {
        if (error.response?.status === 404) {
            return null;
        }
        throw error;
    }
};

const fetchEstatisticas = async () => {
    const response = await api.get('/profissionais/estatisticas');
    return extractData(response);
};

const fetchHealth = async () => {
    const response = await api.get('/profissionais/health');
    return extractData(response);
};

// ==============================================================================
// ✅ HOOKS DE CONSULTA
// ==============================================================================

const useProfissionaisQuery = (params = {}, options = {}) => {
    return useQuery({
        queryKey: [PROFISSIONAIS_QUERY_KEY, 'list', params],
        queryFn: () => fetchProfissionais(params),
        staleTime: 2 * 60 * 1000,
        keepPreviousData: true,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
            if ([401, 403, 404].includes(error.response?.status)) {
                return false;
            }
            return failureCount < 2;
        },
        ...options
    });
};

const useProfissionalByIdQuery = (id, options = {}) => {
    return useQuery({
        queryKey: [PROFISSIONAIS_QUERY_KEY, id],
        queryFn: () => fetchProfissionalById(id),
        enabled: !!id && (options.enabled !== false),
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        retry: 1,
        ...options
    });
};

const useProfissionaisDisponiveisQuery = (params = {}, options = {}) => {
    return useQuery({
        queryKey: [PROFISSIONAIS_QUERY_KEY, 'disponiveis', params],
        queryFn: () => fetchProfissionaisDisponiveis(params),
        staleTime: 2 * 60 * 1000,
        keepPreviousData: true,
        ...options
    });
};

const useProfissionaisPorEspecialidadeQuery = (especialidade, params = {}, options = {}) => {
    return useQuery({
        queryKey: [PROFISSIONAIS_QUERY_KEY, 'especialidade', especialidade, params],
        queryFn: () => fetchProfissionaisPorEspecialidade(especialidade, params),
        enabled: !!especialidade && (options.enabled !== false),
        staleTime: 5 * 60 * 1000,
        ...options
    });
};

const useProfissionaisPorServicoQuery = (servicoId, params = {}, options = {}) => {
    return useQuery({
        queryKey: [PROFISSIONAIS_QUERY_KEY, 'servico', servicoId, params],
        queryFn: () => fetchProfissionaisPorServico(servicoId, params),
        enabled: !!servicoId && (options.enabled !== false),
        staleTime: 5 * 60 * 1000,
        ...options
    });
};

const useProfissionalPorUsuarioQuery = (userId, options = {}) => {
    return useQuery({
        queryKey: [PROFISSIONAIS_QUERY_KEY, 'usuario', userId],
        queryFn: () => fetchProfissionalPorUsuario(userId),
        enabled: !!userId && (options.enabled !== false),
        staleTime: 5 * 60 * 1000,
        retry: false,
        ...options
    });
};

const useEstatisticasQuery = (options = {}) => {
    return useQuery({
        queryKey: [PROFISSIONAIS_QUERY_KEY, 'estatisticas'],
        queryFn: fetchEstatisticas,
        staleTime: 10 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        ...options
    });
};

const useProfissionaisHealthQuery = (options = {}) => {
    return useQuery({
        queryKey: [PROFISSIONAIS_QUERY_KEY, 'health'],
        queryFn: fetchHealth,
        staleTime: 60 * 1000,
        retry: false,
        ...options
    });
};

// ==============================================================================
// ✅ HOOK DE MUTAÇÕES
// ==============================================================================

const useProfissionaisMutations = (currentUser = null) => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();

    const invalidateQueries = (profissionalId = null) => {
        queryClient.invalidateQueries({ queryKey: [PROFISSIONAIS_QUERY_KEY] });
        
        if (profissionalId) {
            const validId = validateId(profissionalId);
            if (validId) {
                queryClient.invalidateQueries({ queryKey: [PROFISSIONAIS_QUERY_KEY, validId] });
            }
        }
        
        queryClient.invalidateQueries({ queryKey: [PROFISSIONAIS_QUERY_KEY, 'disponiveis'] });
        queryClient.invalidateQueries({ queryKey: [PROFISSIONAIS_QUERY_KEY, 'estatisticas'] });
    };

    const createProfissional = useMutation({
        mutationFn: async (data) => {
            const payload = {
                userId: data.userId,
                especialidades: data.especialidades || [],
                registroProfissional: data.registroProfissional,
                status: data.status || 'ativo',
                disponivelParaAgendamento: data.disponivelParaAgendamento !== false,
                metadata: data.metadata || {}
            };
            
            if (data.accountId) {
                payload.accountId = data.accountId;
            }
            
            const response = await api.post('/profissionais', payload);
            return response.data;
        },
        onSuccess: (data) => {
            invalidateQueries(data.data?.id);
            if (data.data?.user?.id) {
                queryClient.invalidateQueries({ queryKey: ['users', data.data.user.id] });
            }
            setAlert(data.message || 'Profissional criado com sucesso!', 'success');
        },
        onError: (error) => {
            const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Erro ao criar profissional';
            setAlert(errorMessage, 'error');
        }
    });

    const updateProfissional = useMutation({
        mutationFn: async ({ profissionalId, data }) => {
            const validId = validateId(profissionalId, 'atualização');
            if (!validId) throw new Error('ID inválido');
            
            const payload = {
                especialidades: data.especialidades,
                registroProfissional: data.registroProfissional,
                status: data.status,
                disponivelParaAgendamento: data.disponivelParaAgendamento,
                metadata: data.metadata
            };
            
            const response = await api.put(`/profissionais/${validId}`, payload);
            return response.data;
        },
        onSuccess: (data, variables) => {
            invalidateQueries(variables.profissionalId);
            setAlert(data.message || 'Profissional atualizado com sucesso!', 'success');
        },
        onError: (error) => {
            const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Erro ao atualizar profissional';
            setAlert(errorMessage, 'error');
        }
    });

    const adicionarEspecialidade = useMutation({
        mutationFn: async ({ profissionalId, especialidade }) => {
            const validId = validateId(profissionalId, 'adicionar especialidade');
            if (!validId) throw new Error('ID inválido');
            if (!especialidade || especialidade.trim() === '') {
                throw new Error('Especialidade é obrigatória');
            }
            
            const response = await api.post(`/profissionais/${validId}/especialidades`, { especialidade });
            return response.data;
        },
        onSuccess: (data, variables) => {
            invalidateQueries(variables.profissionalId);
            setAlert(data.message || 'Especialidade adicionada com sucesso!', 'success');
        },
        onError: (error) => {
            const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Erro ao adicionar especialidade';
            setAlert(errorMessage, 'error');
        }
    });

    const removerEspecialidade = useMutation({
        mutationFn: async ({ profissionalId, especialidade }) => {
            const validId = validateId(profissionalId, 'remover especialidade');
            if (!validId) throw new Error('ID inválido');
            if (!especialidade) throw new Error('Especialidade é obrigatória');
            
            const response = await api.delete(`/profissionais/${validId}/especialidades/${encodeURIComponent(especialidade)}`);
            return response.data;
        },
        onSuccess: (data, variables) => {
            invalidateQueries(variables.profissionalId);
            setAlert(data.message || 'Especialidade removida com sucesso!', 'success');
        },
        onError: (error) => {
            const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Erro ao remover especialidade';
            setAlert(errorMessage, 'error');
        }
    });

    const ativarProfissional = useMutation({
        mutationFn: async (profissionalId) => {
            const validId = validateId(profissionalId, 'ativar');
            if (!validId) throw new Error('ID inválido');
            
            const response = await api.post(`/profissionais/${validId}/ativar`);
            return response.data;
        },
        onSuccess: (data, variables) => {
            invalidateQueries(variables);
            setAlert(data.message || 'Profissional ativado com sucesso!', 'success');
        },
        onError: (error) => {
            const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Erro ao ativar profissional';
            setAlert(errorMessage, 'error');
        }
    });

    const desativarProfissional = useMutation({
        mutationFn: async ({ profissionalId, motivo }) => {
            const validId = validateId(profissionalId, 'desativar');
            if (!validId) throw new Error('ID inválido');
            
            const response = await api.post(`/profissionais/${validId}/desativar`, { motivo });
            return response.data;
        },
        onSuccess: (data, variables) => {
            invalidateQueries(variables.profissionalId);
            setAlert(data.message || 'Profissional desativado com sucesso!', 'success');
        },
        onError: (error) => {
            const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Erro ao desativar profissional';
            setAlert(errorMessage, 'error');
        }
    });

    const colocarEmFerias = useMutation({
        mutationFn: async (profissionalId) => {
            const validId = validateId(profissionalId, 'férias');
            if (!validId) throw new Error('ID inválido');
            
            const response = await api.post(`/profissionais/${validId}/ferias`);
            return response.data;
        },
        onSuccess: (data, variables) => {
            invalidateQueries(variables);
            setAlert(data.message || 'Profissional em férias!', 'success');
        },
        onError: (error) => {
            const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Erro ao colocar profissional em férias';
            setAlert(errorMessage, 'error');
        }
    });

    const retornarFerias = useMutation({
        mutationFn: async (profissionalId) => {
            const validId = validateId(profissionalId, 'retornar férias');
            if (!validId) throw new Error('ID inválido');
            
            const response = await api.post(`/profissionais/${validId}/retornar-ferias`);
            return response.data;
        },
        onSuccess: (data, variables) => {
            invalidateQueries(variables);
            setAlert(data.message || 'Profissional retornou de férias!', 'success');
        },
        onError: (error) => {
            const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Erro ao retornar profissional de férias';
            setAlert(errorMessage, 'error');
        }
    });

    const atualizarConfiguracoesAgenda = useMutation({
        mutationFn: async ({ profissionalId, configuracoes }) => {
            const validId = validateId(profissionalId, 'atualizar agenda');
            if (!validId) throw new Error('ID inválido');
            
            const response = await api.put(`/profissionais/${validId}/configuracoes-agenda`, configuracoes);
            return response.data;
        },
        onSuccess: (data, variables) => {
            invalidateQueries(variables.profissionalId);
            setAlert(data.message || 'Configurações da agenda atualizadas!', 'success');
        },
        onError: (error) => {
            const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Erro ao atualizar configurações';
            setAlert(errorMessage, 'error');
        }
    });

    return {
        createProfissional,
        updateProfissional,
        adicionarEspecialidade,
        removerEspecialidade,
        ativarProfissional,
        desativarProfissional,
        colocarEmFerias,
        retornarFerias,
        atualizarConfiguracoesAgenda,

        isPending: 
            createProfissional?.isPending ||
            updateProfissional?.isPending ||
            adicionarEspecialidade?.isPending ||
            removerEspecialidade?.isPending ||
            ativarProfissional?.isPending ||
            desativarProfissional?.isPending ||
            colocarEmFerias?.isPending ||
            retornarFerias?.isPending ||
            atualizarConfiguracoesAgenda?.isPending,

        isCreating: createProfissional?.isPending || false,
        isUpdating: updateProfissional?.isPending || false,
        isAddingEspecialidade: adicionarEspecialidade?.isPending || false,
        isRemovingEspecialidade: removerEspecialidade?.isPending || false,
        isChangingStatus: ativarProfissional?.isPending || desativarProfissional?.isPending || false,
        isUpdatingAgenda: atualizarConfiguracoesAgenda?.isPending || false
    };
};

// ==============================================================================
// ✅ HOOK PRINCIPAL
// ==============================================================================

const useProfissionais = () => {
    const mutations = useProfissionaisMutations();

    return {
        useProfissionaisQuery,
        useProfissionalByIdQuery,
        useProfissionaisDisponiveisQuery,
        useProfissionaisPorEspecialidadeQuery,
        useProfissionaisPorServicoQuery,
        useProfissionalPorUsuarioQuery,
        useEstatisticasQuery,
        useProfissionaisHealthQuery,
        ...mutations,
        buildQueryParams,
        extractData,
        validateId,
        formatProfissionalData
    };
};

// ==============================================================================
// ✅ EXPORTAÇÕES (LIVRE DE CONFLITOS WEB-PACK)
// ==============================================================================

export default useProfissionais;

export {
    useProfissionaisQuery, // ✅ USADO DIRETO NA PÁGINA
    useProfissionalByIdQuery as useProfissionalById,
    useProfissionaisDisponiveisQuery as useProfissionaisDisponiveis,
    useProfissionaisPorEspecialidadeQuery as useProfissionaisPorEspecialidade,
    useProfissionaisPorServicoQuery as useProfissionaisPorServico,
    useProfissionalPorUsuarioQuery as useProfissionalPorUsuario,
    useEstatisticasQuery as useEstatisticas,
    useProfissionaisHealthQuery as useProfissionaisHealth,
    useProfissionaisMutations,
    buildQueryParams,
    extractData,
    validateId,
    formatProfissionalData
};